import {useState} from "react";
import {useTranslation} from "react-i18next";
import {toast} from "react-toastify";
import {joinWithoutDuplicates} from "../../../utils/StringUtils";

import type ApiResponse from "../../../services/api/types/ApiResponse";
import type AcceptedMast from "../../../types/AcceptedMast";
import ConstructionStatus from "../../../types/ConstructionStatus";
import useConstructionNewHook from "../../../hooks/useConstructionNewHook";
import type MastLocation from "../../../types/MastLocation";
import type ConstructionAllDetailsGuiDto from "../../../dto/construction/ConstructionAllDetailsGuiDto";
import type CalculationResultDto from "../../../dto/calculations/CalculationResultDto";
import type MastResultDto from "../../../dto/calculations/MastResultDto";
import useCalculationNewHook from "../../../hooks/useCalculationNewHook";

const useConstructionCalculationStateChangeHook = (
  construction: ConstructionAllDetailsGuiDto | undefined,
  selectedCalculationResults: CalculationResultDto | undefined,
): {
  handleConfirm: () => Promise<void>;
  handleDismiss: () => void;
  handleOpen: () => Promise<void>;
  open: boolean;
} => {
  const [open, setOpen] = useState<boolean>(false);
  const [constructionIds, setConstructionIds] = useState<string[]>([]);
  const [constructionIdsUnaccepted, setConstructionIdsUnaccepted] = useState<string[]>([]);
  const [mastIdsPerMast, setMastIdsPerMast] = useState<Record<string, string[]>>({});

  const {t} = useTranslation(["constructionCalculations"]);
  const {getAcceptedMast, getMastLocationsInRange, accept, unaccept} = useConstructionNewHook();
  const {getAcceptedCalculationByConstructionId} = useCalculationNewHook();

  const handleDismiss = (): void => {
    setOpen(prevState => !prevState);
  };

  const handleOpen = async (): Promise<void> => {
    if (!construction) return;
    if (!selectedCalculationResults) return;

    const {status} = construction;
    const masts = construction.constructionParts?.mastParts?.masts ?? [];
    const {mastResults} = selectedCalculationResults;
    const promises: Array<Promise<ApiResponse<AcceptedMast[]>>> = [];
    const mastLocationPromises: Array<Promise<ApiResponse<MastLocation[]>>> = [];

    // logic if there is already a mast accepted (in another construction), while the mast (result) in this calculation result is not accepted (yet). 
    // For example when it was calculated before it got accepted in another construction
    masts.forEach(mast => {
      const mastCoordinates = construction.constructionParts?.mastParts?.mastCoordinates.find(mc => mc.id === mast.id);
      if(!mastCoordinates) throw new Error("No coordinates for mast!");

      const xCoordinate = Number(mastCoordinates.x.value);
      const yCoordinate = Number(mastCoordinates.y.value);
      const id = `${mast.no.value.replace("/", "-")}:${xCoordinate}:${yCoordinate}`;

      promises.push(getAcceptedMast(id));
    });

    const responses = await Promise.all(promises);
    const firstPreviouslyAcceptedMast =
      responses.find(r => r.data?.[0]?.isAccepted)?.data?.[0] ?? undefined;
    const matchingMastResult = mastResults.find(
      (m: MastResultDto) => firstPreviouslyAcceptedMast?.number === m.mastNo,
    );

    if (
      status !== ConstructionStatus.Accepted &&
      matchingMastResult !== undefined &&
      !matchingMastResult.isAccepted
    ) {
      toast(
        t("mastAcceptedWarning", {
          mastId: matchingMastResult.mastNo,
          interpolation: {escapeValue: false},
        }),
        {type: "warning"},
      );
      return;
    }

    masts.forEach(mast => {
      const mastCoordinates = construction.constructionParts?.mastParts?.mastCoordinates.find(mc => mc.id === mast.id);
      if(!mastCoordinates) throw new Error("No coordinates for mast!");

      const xCoordinate = Number(mastCoordinates.x.value);
      const yCoordinate = Number(mastCoordinates.y.value);
      const mastNumber = `${mast.no.value.replace("/", "-")}`;

      mastLocationPromises.push(
        getMastLocationsInRange(mastNumber, xCoordinate, yCoordinate),
      );
    });
    const mastLocationResponses = await Promise.all(mastLocationPromises);

    mastLocationResponses.forEach(r => {
      const {data} = r;
      if (data !== null) {
        data.forEach(mastLocation => {
          setConstructionIds(prevState =>
            joinWithoutDuplicates(
              prevState,
              mastResults.every((mr: MastResultDto) => mr.isAccepted) // If all masts are already accepted, we only unaccept the current construction
                ? [construction?.id]
                : [mastLocation.constructionId],
            ),
          );
          setConstructionIdsUnaccepted(prevState =>
            joinWithoutDuplicates(
              prevState,
              [mastLocation.constructionId],
            ),
          );
          setMastIdsPerMast(prev =>
            addToRecordImmutable(prev, mastLocation.no, mastLocation.id),
          );
        });
      }
    });

    function addToRecordImmutable(
      record: Record<string, string[]>,
      key: string,
      value: string,
    ): Record<string, string[]> {
      const existing = record[key] ?? [];
      if (existing.includes(value)) {
        return record; // No change needed
      }

      return {
        ...record,
        [key]: [...existing, value],
      };
    }

    handleDismiss();
  };

  const handleConfirm = async (): Promise<void> => {
    if (construction === undefined) return;
    const {status} = construction;

    if (status === ConstructionStatus.Accepted) {
      await handleUnaccept();
    } else {
      await handleAccept();
    }
  };

  const handleUnaccept = async (): Promise<void> => {
    if (!construction) return;
    
    const {id} = construction;

    const result = await getAcceptedCalculationByConstructionId(id);
    const calculationId = result.data?.calculationId;

    if (!calculationId) throw new Error("No calculation found for construction!");

    await unaccept(
      id,
      calculationId,
      constructionIdsUnaccepted,
      mastIdsPerMast,
    );
  };

  const handleAccept = async (): Promise<void> => {
    if (!construction) return;
    if (!selectedCalculationResults) return;

    const {id} = construction;
    const {
      calculationId,
      endedOn,
    } = selectedCalculationResults;

    const index = constructionIds.findIndex(i => i === id);

    if (index !== 0) {
      const removed = constructionIds.splice(index, 1);
      constructionIds.unshift(removed[0]);
    }

    await accept(
      construction.id,
      calculationId,
      endedOn ?? "",
      constructionIds,
      mastIdsPerMast,
    );
   
  };

  return {
    open,
    handleDismiss,
    handleConfirm,
    handleOpen,
  };
};

export default useConstructionCalculationStateChangeHook;
